import { useState, useEffect, useCallback, useRef } from 'react';
import LevelUpOverlay from './LevelUpOverlay';
import BadgeToast from './BadgeToast';
import LogoToast from './LogoToast';
import MissionCompleteToast from './MissionCompleteToast';
import AnimalDiscoveryOverlay from './AnimalDiscoveryOverlay';
import AnimalRestoredToast from './AnimalRestoredToast';
import { catalogText } from '@/utils/catalog';

interface ScanResult {
  amenityName: string;
  buildingName: string;
  levelUp: {
    leveledUp: boolean;
    previousLevel: number;
    newLevel: number;
    levelName: string;
  };
  newBadges: Array<{ id: string; slug: string; name: string; rarity: string; xpReward: number }>;
  completedMissions: Array<{ id: string; slug?: string; name: string; xpReward: number }>;
  newLogo?: { tenantName: string; logoUrl: string | null };
  animalDiscovery?: {
    discovered: boolean;
    restored: boolean;
    animalName: string;
    animalSlug: string;
    fieldNote: string;
    imagePath: string;
    thumbPath: string;
    habitat: string;
    milestone?: number;
    buildingComplete?: { buildingName: string; speciesCount: number };
  };
}

interface CelebrationOrchestratorProps {
  result: ScanResult;
}

type CelebrationEvent =
  | { type: 'levelUp'; newLevel: number; levelName: string }
  | { type: 'logo'; logo: { tenantName: string; logoUrl: string | null } }
  | { type: 'badge'; badge: { name: string; rarity: string; xpReward: number } }
  | { type: 'mission'; mission: { name: string; xpReward: number } }
  | {
      type: 'animal_discovery';
      animal: {
        name: string;
        slug: string;
        fieldNote: string;
        imagePath: string;
        thumbPath?: string;
        habitat: string;
        milestone?: number;
      };
      amenityName: string;
      buildingName: string;
    }
  | { type: 'animal_restored'; animal: { name: string; slug: string } }
  | { type: 'building_complete'; buildingName: string; speciesCount: number };

export default function CelebrationOrchestrator({ result }: CelebrationOrchestratorProps) {
  const [currentEvent, setCurrentEvent] = useState<CelebrationEvent | null>(null);
  const queueRef = useRef<CelebrationEvent[]>([]);
  const processingRef = useRef(false);

  // Build the event queue once on mount
  useEffect(() => {
    const events: CelebrationEvent[] = [];

    if (result.levelUp.leveledUp) {
      events.push({
        type: 'levelUp',
        newLevel: result.levelUp.newLevel,
        levelName: result.levelUp.levelName,
      });
    }

    if (result.animalDiscovery?.discovered) {
      events.push({
        type: 'animal_discovery',
        animal: {
          name: result.animalDiscovery.animalName,
          slug: result.animalDiscovery.animalSlug,
          fieldNote: result.animalDiscovery.fieldNote,
          imagePath: result.animalDiscovery.imagePath,
          thumbPath: result.animalDiscovery.thumbPath,
          habitat: result.animalDiscovery.habitat,
          milestone: result.animalDiscovery.milestone,
        },
        amenityName: result.amenityName,
        buildingName: result.buildingName,
      });
    }

    if (result.animalDiscovery?.buildingComplete) {
      events.push({
        type: 'building_complete',
        buildingName: result.animalDiscovery.buildingComplete.buildingName,
        speciesCount: result.animalDiscovery.buildingComplete.speciesCount,
      });
    }

    if (result.animalDiscovery?.restored) {
      events.push({
        type: 'animal_restored',
        animal: {
          name: result.animalDiscovery.animalName,
          slug: result.animalDiscovery.animalSlug,
        },
      });
    }

    if (result.newLogo) {
      events.push({
        type: 'logo',
        logo: result.newLogo,
      });
    }

    for (const badge of result.newBadges) {
      events.push({
        type: 'badge',
        badge: { name: catalogText(badge.slug, 'name', badge.name), rarity: badge.rarity, xpReward: badge.xpReward },
      });
    }

    for (const mission of result.completedMissions) {
      events.push({
        type: 'mission',
        mission: { name: catalogText(mission.slug, 'name', mission.name), xpReward: mission.xpReward },
      });
    }

    if (events.length === 0) return;

    queueRef.current = events;

    // Start after a brief delay
    const timer = setTimeout(() => processNext(), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processNext = useCallback(() => {
    if (processingRef.current) return;

    const next = queueRef.current.shift();
    if (!next) {
      setCurrentEvent(null);
      return;
    }

    processingRef.current = true;
    setCurrentEvent(next);
  }, []);

  const handleDismiss = useCallback(() => {
    setCurrentEvent(null);
    processingRef.current = false;

    // Wait a beat before showing the next
    const delay = queueRef.current.length > 0 ? 500 : 0;
    if (delay > 0) {
      setTimeout(() => processNext(), delay);
    }
  }, [processNext]);

  if (!currentEvent) return null;

  return (
    <>
      {currentEvent.type === 'levelUp' && (
        <LevelUpOverlay
          visible
          newLevel={currentEvent.newLevel}
          levelName={currentEvent.levelName}
          onDismiss={handleDismiss}
        />
      )}
      {currentEvent.type === 'logo' && <LogoToast visible logo={currentEvent.logo} onDismiss={handleDismiss} />}
      {currentEvent.type === 'badge' && <BadgeToast visible badge={currentEvent.badge} onDismiss={handleDismiss} />}
      {currentEvent.type === 'mission' && (
        <MissionCompleteToast visible mission={currentEvent.mission} onDismiss={handleDismiss} />
      )}
      {currentEvent.type === 'animal_discovery' && (
        <AnimalDiscoveryOverlay
          visible
          animal={currentEvent.animal}
          amenityName={currentEvent.amenityName}
          buildingName={currentEvent.buildingName}
          onDismiss={handleDismiss}
        />
      )}
      {currentEvent.type === 'animal_restored' && (
        <AnimalRestoredToast visible animal={currentEvent.animal} onDismiss={handleDismiss} />
      )}
      {currentEvent.type === 'building_complete' && (
        <div className="fixed top-4 inset-x-4 z-50" role="alert">
          <button
            onClick={handleDismiss}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gold/10 border border-gold/30 shadow-lg"
          >
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-lg">🏆</div>
            <div className="flex-1 text-start min-w-0">
              <p className="text-small font-bold text-gold truncate">
                All {currentEvent.speciesCount} species in {currentEvent.buildingName} discovered!
              </p>
            </div>
          </button>
        </div>
      )}
    </>
  );
}
